import { useCallback } from 'react';
import { throttle } from 'underscore';
import { useDispatch } from 'react-redux';
import { useIsAdmin } from 'js/hooks/user';
import { prop } from 'js/utils/ramda.util';
import { useLocation } from 'react-router-dom';
import { useTranslations } from 'js/hooks/i18n';
import { useUserSelector } from 'js/store/hooks';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';
import { useNavigateToPrevPage } from 'js/components/system/Router/components/PageRouter/hooks';

import * as helpers from '../helpers';
import { useOpenGame } from './useOpenGame';
import { GAME_LAUNCH_TYPE } from '../constants';
import { useGetGameConfig } from './useGetGameConfig';
import { useLaunchGameByCasino } from './useLaunchGameByCasino';
import { useHandleSuccessLogin } from './useHandleSuccessLogin';

export const useLaunchGame = () => {
    const isAdmin = useIsAdmin();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const translate = useTranslations('playtech.mobile.system.launcher.gameLaunchPopup.');
    const showLoginPopup = useLoginPopup();
    const navigateToPrevPage = useNavigateToPrevPage();
    const getGameConfig = useGetGameConfig();
    const handleSuccessLogin = useHandleSuccessLogin();
    const openGame = useOpenGame();
    const launchGameByCasino = useLaunchGameByCasino();

    /**
     * @param launchOptions {Object}
     * @param launchOptions.code {String}
     * @param [launchOptions.mode=1] {Number} 1 - real mode, 0 - demo mode
     * @param [launchOptions.queryString] {String} Additional URL query params
     * @param [launchOptions.gameLaunchType] {String}
     * @param [launchOptions.onGameLaunched] {Function}
     * @param [launchOptions.isReactGame] {Boolean} For game launch tracking
     * @param [launchOptions.container] {Boolean} For game launch tracking
     * @param [launchOptions.gameFeed] {Boolean} For game launch tracking
     * @param [launchOptions.gameName] {Boolean} For game launch tracking
     * @param [launchOptions.feedName] {String} For game launch tracking
     * @param [launchOptions.webContentId] {Boolean} For game launch tracking
     */
    const launchGame = useCallback((launchOptions) => {
        const {
            gameLaunchType,
            code: gameCode,
            mode: propsMode,
            queryString: additionalQueryString,
            ...options
        } = launchOptions;

        if (isAdmin) {
            dispatch(showCommonPopupAction({
                title: translate('title'),
                content: translate('content')
            }));

            return;
        }

        if (gameLaunchType === GAME_LAUNCH_TYPE.CASINO) {
            launchGameByCasino(launchOptions);

            return;
        }

        const gameConfig = getGameConfig(gameCode);

        if (!gameConfig) {
            if (helpers.isGamingPath(pathname)) {
                navigateToPrevPage();
            }

            return;
        }

        const mode = helpers.getActualMode({
            isLoggedIn,
            mode: propsMode,
            funSupport: gameConfig.funSupport
        });

        options.gameName = options.gameName || gameConfig.name;

        if (mode && !isLoggedIn) {
            showLoginPopup({
                onLogin: () => handleSuccessLogin({ gameCode, mode, additionalQueryString, ...options })
            });

            return;
        }

        openGame({ gameCode, mode, additionalQueryString, ...options });
    }, [
        translate, dispatch, isAdmin, launchGameByCasino,
        isLoggedIn, showLoginPopup, navigateToPrevPage,
        getGameConfig, handleSuccessLogin, openGame, pathname
    ]);

    return throttle((launchOptions) => launchGame(launchOptions), 500);
};
